import axiosInstance, { endpoints } from 'src/lib/axios';
import { IBokslutProcess, BokslutStegNyckel, BokslutStegStatus } from 'src/types/nya/ekonomi/bokforing/bokslutTyper';
import { IRäkenskapsår } from 'src/types/nya/common';

const parseProcess = (data: IBokslutProcess): IBokslutProcess => {
    if (typeof data.stegStatus === 'string') {
        try {
            data.stegStatus = JSON.parse(data.stegStatus);
        } catch (e) {
            console.error('Failed to parse stegStatus string', e);
            data.stegStatus = {} as any;
        }
    }
    return data;
};

export function hämtaEllerSkapaBokslutProcess(
    orgId: string,
    rakenskapsar: IRäkenskapsår
): Promise<IBokslutProcess> {
    
    const getSafeDate = (d: any) => {
        if (!d) return '';
        if (typeof d === 'string') return d.substring(0, 10);
        if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
        if (d instanceof Date) return d.toISOString().substring(0, 10);
        return String(d).substring(0, 10);
    };

    if (!rakenskapsar) {
        return Promise.reject(new Error('Inget rakenskapsar angivet.'));
    }

    const start = getSafeDate(rakenskapsar.förstaDag);
    const slut = getSafeDate(rakenskapsar.sistaDag);

    if (!start || !slut) {
        return Promise.reject(new Error('Kunde inte identifiera start- eller slutdatum för rakenskapsaret.'));
    }


    return axiosInstance
        .get<IBokslutProcess>(endpoints.bokföring.bokslutProcess.hämtaEllerSkapa(orgId, start, slut))
        .then((r) => {
            return parseProcess(r.data);
        })
        .catch(err => {
            console.error('BokslutProcess fetch error:', err);
            throw err;
        });
}

export function uppdateraBokslutProcessSteg(
    processId: string,
    aktivtSteg: number,
    stegNyckel?: BokslutStegNyckel,
    stegStatus?: BokslutStegStatus
): Promise<IBokslutProcess> {
    return axiosInstance
        .put<IBokslutProcess>(endpoints.bokföring.bokslutProcess.uppdateraSteg(processId), {
            aktivtSteg,
            stegNyckel: stegNyckel ?? null,
            stegStatus: stegStatus ?? null,
        })
        .then((r) => parseProcess(r.data));
}

export function slutförBokslutProcess(processId: string): Promise<IBokslutProcess> {
    return axiosInstance
        .post<IBokslutProcess>(endpoints.bokföring.bokslutProcess.slutför(processId))
        .then((r) => parseProcess(r.data));
}

import { BokslutDto } from 'src/types/nya/ekonomi/bokforing/bokslutTyper';

export function hämtaAllaBokslut(orgId: string): Promise<BokslutDto[]> {
    return axiosInstance
        .get<BokslutDto[]>(endpoints.bokföring.bokslutV2.hämtaAlla(orgId))
        .then((r) => r.data);
}

export function beräknaOchSparaBokslut(
    orgId: string,
    start: string,
    slut: string
): Promise<BokslutDto> {
    return axiosInstance
        .post<BokslutDto>(endpoints.bokföring.bokslutV2.berakna(orgId, start, slut))
        .then((r) => r.data);
}

/** Returns a blob URL that the browser can open/download as PDF. */
export async function hämtaBokslutPdf(
    orgId: string,
    start: string,
    slut: string
): Promise<string> {
    const response = await axiosInstance.get<Blob>(
        endpoints.bokföring.bokslutV2.pdf(orgId, start, slut),
        { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
}

// --- Årsredovisning: underlag (aktiebok, styrelse, allmänt om verksamheten m.m.) ---

export type AktieägareDto = {
    förnamn: string;
    efternamn: string;
    personnummer?: string;
    antalAktier: number;
    andelProcent: number;
};

export type AktiebokDto = {
    aktieägare: AktieägareDto[];
};

export type StyrelsemedlemDto = {
    förnamn: string;
    efternamn: string;
    personnummer?: string;
    roll?: string;
    signerar: boolean;
};

export type VäsentligHändelseDto = {
    rubrik: string;
    beskrivning: string;
    /** YYYY-MM-DD, valfritt. */
    datum?: string;
};

export type UnderhållspostDto = {
    typ: 'UTFÖRT' | 'PLANERAT';
    år: number;
    beskrivning: string;
    belopp?: number;
};

export type ÅrsredovisningsunderlagDto = {
    allmäntOmVerksamheten: string[];
    styrelse: StyrelsemedlemDto[];
    datumFörÅrsstämma?: string;
    aktieutdelning?: number;
    aktiekapitalVidÅretsIngång?: number;
    väsentligaHändelser?: VäsentligHändelseDto[];
    underhållsposter?: UnderhållspostDto[];
};

export type VinstdispositionDto = {
    åretsResultat: number;
    föreslagenAktieutdelning: number;
    aktiekapitalVidÅretsIngång: number;
    balanseratResultat?: number;
};

export function hämtaVinstdisposition(
    orgId: string,
    start: string,
    slut: string
): Promise<VinstdispositionDto> {
    return axiosInstance
        .get<VinstdispositionDto>(
            endpoints.bokföring.bokslutV2.vinstdisposition(orgId, start, slut)
        )
        .then((r) => r.data);
}

export function hämtaAktiebok(orgId: string): Promise<AktiebokDto> {
    return axiosInstance
        .get<AktiebokDto>(endpoints.bokföring.bokslutV2.aktiebok(orgId))
        .then((r) => r.data);
}

export function sparaAktiebok(orgId: string, aktiebok: AktiebokDto): Promise<void> {
    return axiosInstance
        .post(endpoints.bokföring.bokslutV2.aktiebok(orgId), aktiebok)
        .then(() => undefined);
}

export function hämtaÅrsredovisningsunderlag(
    orgId: string,
    start: string,
    slut: string
): Promise<ÅrsredovisningsunderlagDto> {
    return axiosInstance
        .get<ÅrsredovisningsunderlagDto>(
            endpoints.bokföring.bokslutV2.arsredovisningUnderlag(orgId, start, slut)
        )
        .then((r) => r.data);
}

export function sparaÅrsredovisningsunderlag(
    orgId: string,
    start: string,
    slut: string,
    underlag: ÅrsredovisningsunderlagDto
): Promise<void> {
    return axiosInstance
        .post(
            endpoints.bokföring.bokslutV2.arsredovisningUnderlag(orgId, start, slut),
            underlag
        )
        .then(() => undefined);
}

/** Generates the full årsredovisning PDF and returns a blob URL. */
export async function hämtaÅrsredovisningPdf(
    orgId: string,
    start: string,
    slut: string
): Promise<string> {
    const response = await axiosInstance.get<Blob>(
        endpoints.bokföring.bokslutV2.arsredovisningPdf(orgId, start, slut),
        { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
}

// --- Data import för årsredovisning ---

export type FlerårsöversiktRadDto = {
    år: string;
    nettoomsättning: number | null;
    resultat: number | null;
    soliditetProcent: number | null;
};

export type ImporteradResultaträkningDto = {
    nettoomsättning: number | null;
    personalkostnader: number | null;
    övrigaExternaKostnader: number | null;
    rörelseresultat: number | null;
    räntekostnader: number | null;
    ränteintäkter: number | null;
    resultatEfterFinansiellaPoster: number | null;
    skattPåÅretsResultat: number | null;
    åretsResultat: number | null;
};

export type ImporteradBalansräkningTillgångarDto = {
    kundfordringar: number | null;
    övrigaKortfristigaFordringar: number | null;
    kassaOchBank: number | null;
    förutbetaldaFörsäkringar: number | null;
    upplupnaIntäkter: number | null;
    summaTillgångar: number | null;
};

export type ImporteradBalansräkningEgetKapitalOchSkulderDto = {
    aktiekapital: number | null;
    balanseratResultat: number | null;
    åretsResultat: number | null;
    upplupnaKostnader: number | null;
    arbetsgivaravgiftSkulder: number | null;
    momsSkulder: number | null;
    skatteSkulder: number | null;
    summaEgetKapitalOchSkulder: number | null;
};

export type ImporteradeFörändingarIEgetKapitalDto = {
    aktiekapitalVidIngång: number | null;
    aktiekapitalVidUtgång: number | null;
    balanseratResultatVidIngång: number | null;
    utdelning: number | null;
    balanserasINyRäkning: number | null;
    balanseratResultatVidUtgång: number | null;
    åretsResultatVidIngång: number | null;
    åretsResultat: number | null;
    åretsResultatVidUtgång: number | null;
};

export type DataImportFörÅrsredovisningDto = {
    flerårsöversikt: FlerårsöversiktRadDto[] | null;
    resultaträkning: ImporteradResultaträkningDto | null;
    balansräkningTillgångar: ImporteradBalansräkningTillgångarDto | null;
    balansräkningEgetKapitalOchSkulder: ImporteradBalansräkningEgetKapitalOchSkulderDto | null;
    förändingarIEgetKapital: ImporteradeFörändingarIEgetKapitalDto | null;
};

export async function importeraÅrsredovisningPdf(
    orgId: string,
    start: string,
    slut: string,
    fil: File
): Promise<DataImportFörÅrsredovisningDto> {
    const formData = new FormData();
    formData.append('fil', fil);
    const response = await axiosInstance.post<DataImportFörÅrsredovisningDto>(
        endpoints.bokföring.bokslutV2.dataimportÅrsredovisning(orgId, start, slut),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
}

export function hämtaDataImport(
    orgId: string,
    start: string,
    slut: string
): Promise<DataImportFörÅrsredovisningDto | null> {
    return axiosInstance
        .get<DataImportFörÅrsredovisningDto>(
            endpoints.bokföring.bokslutV2.dataimportÅrsredovisning(orgId, start, slut)
        )
        .then((r) => r.data ?? null);
}

export function sparaDataImport(
    orgId: string,
    start: string,
    slut: string,
    data: DataImportFörÅrsredovisningDto
): Promise<void> {
    return axiosInstance
        .post(
            endpoints.bokföring.bokslutV2.sparaDataimportÅrsredovisning(orgId, start, slut),
            data
        )
        .then(() => undefined);
}
